import React from 'react';
import { Alert, Button, Icon, InputGroup, Intent, ProgressBar, Spinner, SpinnerSize, Tab, Tabs, TabsExpander } from '@blueprintjs/core';
import ResultItem from './ResultItem';
import InstalledExtensions from './InstalledExtensions';
import { getExtensions } from '../../services/api';
import { useSelector } from 'react-redux';
import { useVariant } from '../../hooks/useVariant';

const ExtensionSidebar = () => {

    const [search, setSearch] = React.useState("");
    const [requesting, setRequesting] = React.useState(false);
    const [searchResults, setSearchResults] = React.useState([]);
    const { installedExtensions } = useSelector(state => state.extensions);

    const [installing, setInstalling] = React.useState(false);
    const [installError, setInstallError] = React.useState(null);
    const [installSuccess, setInstallSuccess] = React.useState(false);
    const [installedExtName, setInstalledExtName] = React.useState(null);

    const variant = useVariant();
    const defaultTag = variant?.extensionTag || '';
    const [showAll, setShowAll] = React.useState(false);

    // Installed state lives on disk only on desktop; on web it is held by the API
    // and already drives the marketplace list.
    const isDesktop = bodapro?.platformType === 'desktop';
    const [selectedTab, setSelectedTab] = React.useState('marketplace');
    const [installedKey, setInstalledKey] = React.useState(0);

    // The variant pins a `tag` (unless "Show all"); the search box is the free-text `label`.
    const effectiveTag = showAll ? '' : defaultTag;

    const getAllExtensions = async (searchTerm = '') => {
        setRequesting(true);
        try{
            const data = await getExtensions(searchTerm, effectiveTag);
            if(data?.results) setSearchResults(data.results);
        }catch(e){
            console.log(e);
        }
        setRequesting(false);
    }

    React.useEffect( () => {
        getAllExtensions();
    }, [showAll, effectiveTag]);

    const installFromZip = async () => {
        const pickerResult = await bodapro.selectFile({
            title: 'Select Extension Zip File',
            buttonLabel: 'Install',
            filters: [{ name: 'Extension Zip', extensions: ['zip'] }]
        });
        if (pickerResult?.canceled || !pickerResult?.filePaths?.length) return;

        const configData = await bodapro.getConfig();
        if (!configData?.configFile) {
            setInstallError('Could not load BodaPro configuration.');
            return;
        }

        setInstalling(true);
        setInstallError(null);
        try {
            const result = await bodapro.installExtensionFromFile({
                zipFilePath: pickerResult.filePaths[0],
                bodaProConfigFile: configData.configFile
            });
            if (result?.status === 'success') {
                setInstallSuccess(true);
                setInstalledExtName(result.extensionName);
                // Remount the installed list so the new extension shows up there
                // even if the user declines the reload.
                setInstalledKey(k => k + 1);
            } else {
                setInstallError(result?.message || 'Installation failed');
            }
        } catch (e) {
            setInstallError(e.message || 'Installation failed');
        } finally {
            setInstalling(false);
        }
    };

    const marketplacePanel = (
        <div style={{ height: '100%' }}>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 6}}>
                <div style={{flex: 1, marginRight: 6}}>
                <InputGroup
                    style={{ width: '100%' }}
                    type="search"
                    className="bp3-input"
                    placeholder="Search extensions"
                    dir="auto"
                    onChange={(event) => {
                        getAllExtensions(event.target.value);
                    }} />
                </div>

                {defaultTag && (
                    <Button
                        small
                        minimal
                        text={showAll ? 'Variant only' : 'Show all'}
                        onClick={() => setShowAll(v => !v)}
                        style={{ whiteSpace: 'nowrap' }}
                    />
                )}
            </div>

            <div style={{ overflowY: 'auto', height: 'calc(100% - 40px)' }}>
                <div style={{ marginRight: 10 }}>
                    {requesting && <Spinner size={SpinnerSize.LARGE}/>}
                    <ResultItem results={searchResults} search={search} installedExtensions={installedExtensions}/>
                </div>
            </div>
        </div>
    );

    return (<div className="p-2" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {installing && <ProgressBar intent={Intent.PRIMARY} />}
        {installError && <div style={{ color: 'red', fontSize: 12, marginBottom: 4 }}>{installError}</div>}

        <div style={{ flex: 1, minHeight: 0 }}>
            {isDesktop ? (
                <Tabs
                    id="extensions-sidebar"
                    selectedTabId={selectedTab}
                    onChange={(tabId) => setSelectedTab(tabId)}
                    renderActiveTabPanelOnly
                    className="extensions-sidebar-tabs"
                >
                    <Tab id="marketplace" title="MARKETPLACE" panel={marketplacePanel} />
                    <Tab id="installed" title="INSTALLED" panel={<InstalledExtensions key={installedKey} />} />
                    <TabsExpander />
                    <Icon
                        icon="upload"
                        size={18}
                        intent={Intent.PRIMARY}
                        title="Install extension from a zip file"
                        style={{ cursor: 'pointer', marginBottom: 6 }}
                        onClick={installFromZip}
                    />
                </Tabs>
            ) : marketplacePanel}
        </div>

        <Alert
            confirmButtonText="Reload Now"
            cancelButtonText="Later"
            isOpen={installSuccess}
            onClose={() => { setInstallSuccess(false); setInstalledExtName(null); }}
            onConfirm={() => { window.location.reload(); }}
            icon="cube"
            intent={Intent.PRIMARY}
        >
            <p>Extension {installedExtName ? `"${installedExtName}"` : ''} installed successfully. Reload BodaPro to activate it.</p>
        </Alert>
    </div>);
}

export default ExtensionSidebar;
